import type { ReactElement } from 'react';
import React from 'react';
import { chunk } from 'lodash';
import groupBy from 'lodash/groupBy';
import { Link } from 'react-router-dom';
import type { MinimalResponse } from 'feature-components/reports/stanine-report';
import UserIcon from 'feature-components/reports/stanine-report/user-icon/user-icon';
import { type BaseGender, useGenders } from 'services/assist-api/entities/gender/genders';
import './users-widget.scss';

type SharedProps = {
  studentsPerIcon: number;
  showIndividualReportLink: boolean;
  generateAssessmentUserLink(id: number): string;
};

const getTitle = (responses: MinimalResponse[]): string =>
  responses
    .map((response) =>
      response.testInstanceUser?.firstname
        ? `${response.testInstanceUser?.firstname} ${response.testInstanceUser?.lastname} ( ${response?.scaleScore} ± ${response?.scaleError} )`
        : `${response?.scaleScore} ± ${response?.scaleError}`
    )
    .join('\n');

type UserProps = {
  response: MinimalResponse[];
  userGender: BaseGender | undefined;
} & SharedProps;

function User({
  response,
  userGender,
  studentsPerIcon,
  showIndividualReportLink,
  generateAssessmentUserLink
}: UserProps): ReactElement {
  const userIcon = (
    <UserIcon
      genderColor={userGender?.name.toLowerCase()}
      incomplete={response.length < studentsPerIcon}
    />
  );

  return (
    <div className="user-icon-wrapper">
      {showIndividualReportLink ? (
        <Link
          key={response[0].id}
          title={getTitle(response)}
          to={generateAssessmentUserLink(response[0].testInstanceUser?.id)}
        >
          {userIcon}
        </Link>
      ) : (
        <span>{userIcon}</span>
      )}
    </div>
  );
}

type UsersWidgetProps = {
  responses: MinimalResponse[];
  splitByGender: boolean;
} & SharedProps;

// This component controls first row of stanine report table
// that displays users
// it is tightly coupled with footer-legend component
export default function UsersWidget({
  responses,
  splitByGender,
  studentsPerIcon,
  showIndividualReportLink,
  generateAssessmentUserLink
}: UsersWidgetProps): ReactElement | null {
  const genders = useGenders().data?.json;

  if (!responses?.length) {
    return null;
  }
  // when widget is filtered by gender, we should introduce grouping by Gender
  //  and introduce keys

  if (splitByGender) {
    const genderGroupedResponses = groupBy(responses, 'testInstanceUser.gender.id');

    return (
      <div>
        {Object.entries(genderGroupedResponses).map(([key, value]) => {
          const userGender = genders?.find((g) => g.id === +key);
          return (
            <div key={key} className="gender-group-block">
              {chunk(value, studentsPerIcon)
                .reverse()
                .map((response) => (
                  <User
                    response={response}
                    userGender={userGender}
                    studentsPerIcon={studentsPerIcon}
                    showIndividualReportLink={showIndividualReportLink}
                    generateAssessmentUserLink={generateAssessmentUserLink}
                  />
                ))}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      {chunk(responses, studentsPerIcon)
        .reverse()
        .map((response) => (
          <User
            response={response}
            userGender={undefined}
            studentsPerIcon={studentsPerIcon}
            showIndividualReportLink={showIndividualReportLink}
            generateAssessmentUserLink={generateAssessmentUserLink}
          />
        ))}
    </div>
  );
}
